import React, { lazy, Suspense, useEffect, useState } from "react";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import LogRocket from "logrocket";
import "./App.css";

import Loader from "./Components/Loader";
import NotFound from "./Components/NotFound/NotFound";
import OAuthSuccess from "./utils/OAuthSuccess";
import OAuthGoogleSignup from "./Pages/Auth/Signup/OAuthGoogleSignup";
import { Box, CircularProgress, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

const Home = lazy(() => import("./Pages/Home/Home"));
const Login = lazy(() => import("./Pages/Auth/Login/Login"));
const Signup = lazy(() => import("./Pages/Auth/Signup/Signup"));
const AppDrawerNav = lazy(() => import("./Pages/CreateNewBot/AppDrawerNav"));
const ChatInterface = lazy(() => import("./Pages/HtmlEmbeds/ChatInterface"));

const SMALL_SIZE = 650;

const ParserDemo = lazy(() => import("./Pages/ParserDemo/ParserDemo"));

function App() {
  const [isSmallDevice, setIsSmallDevice] = useState(
    typeof window !== "undefined" ? window.innerWidth < SMALL_SIZE : false
  );
  const [isBannerDismissed, setIsBannerDismissed] = useState(false);

  useEffect(() => {
    const hasValidAppId = !!process.env.REACT_APP_LOG_ROCKET_PROJECT;
    const urlParams = window.location.href;

    if (hasValidAppId && urlParams.includes("app.queryloop.ai")) {
      LogRocket.init(process.env.REACT_APP_LOG_ROCKET_PROJECT);
    }
  }, []);

  useEffect(() => {
    const onResize = () => setIsSmallDevice(window.innerWidth < SMALL_SIZE);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Routes configuration object
  const routes = [
    {
      path: "/",
      element: <Home />,
    },
    {
      path: "/auth/login",
      element: <Login />,
    },
    {
      path: "/auth/signup",
      element: <Signup />,
    },
    {
      path: "/dashboard/*",
      element: <AppDrawerNav />,
    },
    {
      path: "/chat-interface",
      element: <ChatInterface />,
    },
    {
      path: "/parser-demo",
      element: <ParserDemo />,
    },
    {
      path: "/oauth-success",
      element: <OAuthSuccess />,
    },
    {
      path: "/oauth-google-signup",
      element: <OAuthGoogleSignup />,
    },
    {
      path: "*",
      element: <NotFound />,
    },
  ];

  // Suspense loader component
  const SuspenseLoader = ({ children }) => (
    <Suspense
      fallback={
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
          }}
        >
          <CircularProgress />
        </Box>
      }
    >
      {children}
    </Suspense>
  );

  return (
    <div>
      {isSmallDevice && !isBannerDismissed && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: '100vh',
            zIndex: 2000,
            bgcolor: "rgba(0, 0, 0, 0.9)",
            color: "white",
            textAlign: "center",
            py: 1,
            px: 2,
            pointerEvents: "auto",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box sx={{ px: 4 }}>
          This app is designed for desktop use only and won't work properly on mobile devices. For the best experience, please switch to a computer or tablet.
          </Box>
          <IconButton
            size="small"
            onClick={() => setIsBannerDismissed(true)}
            sx={{
              position: "absolute",
              right: 8,
              top: 4,
              color: "white",
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      )}
      <Router>
        <Suspense fallback={<Loader />}>
          <Routes>
            {routes.map((route, index) => (
              <Route
                key={index}
                path={route.path}
                element={<SuspenseLoader>{route.element}</SuspenseLoader>}
              />
            ))}
          </Routes>
        </Suspense>
      </Router>
    </div>
  );
}

export default App;
